import React from 'react';
import { Link } from 'react-router-dom';
import {
    Eye, Download, ChevronDown, ChevronRight,
    Flame, Leaf, Paperclip, MoreVertical,
    Settings, History, Trash2, Loader2,
    Check, X, FileText, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useState } from 'react';
import { usePermissions, FEATURES } from '@/permissions/usePermissions';

const EmissionsTable = ({
    recentEntriesByDateAdded,
    recentActivity,
    getFYLabel,
    getReportingPeriods,
    selectedYear,
    formatActivityDate,
    formatEntryDate,
    currentOrg,
    deletingEntry,
    handleDeleteEntry,
    handleEditEntry,
    fetchEntryHistory,
    setShowEvidenceUpload,
    handleDeleteEvidence,
    deletingEvidence,
    getCategoryInfo,
    openActionMenu,
    setOpenActionMenu,
    showDeleteConfirm,
    setShowDeleteConfirm,
    totalEntriesCount,
    exportToCSV,
    exportToXLS,
    setShowExportMenu,
    showExportMenu,
    getShortPeriodLabel,
    supabase,
    setShowForm,
    showEvidenceDeleteConfirm,
    setShowEvidenceDeleteConfirm
}) => {
    const { canWithReason } = usePermissions();
    const [sortField, setSortField] = useState('entryDate');
    const [sortDirection, setSortDirection] = useState('desc');

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    if (recentActivity.length === 0 && recentEntriesByDateAdded.length === 0) {
        return (
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
                <div className="text-center py-12">
                    <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 mb-4">No data yet. Start by adding your first entry!</p>
                    <Button
                        onClick={() => setShowForm(true)}
                        variant="outline"
                        className="border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600"
                    >
                        <Flame className="w-4 h-4 mr-2" />
                        Add Your First Entry
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Recent Data Entries
                </h2>
                <div className="flex items-center space-x-3">
                    <Link
                        to="/entries"
                        className="inline-flex items-center px-4 py-2 border border-[#74A57F] text-[#74A57F] rounded-lg hover:bg-[#74A57F]/10 transition-colors text-sm font-medium"
                        data-testid="view-all-entries-btn"
                    >
                        <Eye className="w-4 h-4 mr-2" />
                        All Entries
                    </Link>
                    {recentEntriesByDateAdded.length > 0 && (
                        <div className="relative">
                            <Button
                                onClick={() => setShowExportMenu(!showExportMenu)}
                                variant="outline"
                                className="border-[#003B5C] text-[#003B5C] hover:bg-[#003B5C]/10"
                                data-testid="export-menu-btn"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Export
                                <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showExportMenu ? 'rotate-180' : ''}`} />
                            </Button>

                            {showExportMenu && (
                                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50 export-dropdown">
                                    <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 uppercase">{getShortPeriodLabel()}</div>
                                    <button
                                        onClick={() => {
                                            setShowExportMenu(false);
                                            exportToCSV(selectedYear);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-3"
                                        data-testid="export-csv-btn"
                                    >
                                        <FileText className="w-4 h-4 text-green-600" />
                                        <span>Export as CSV</span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowExportMenu(false);
                                            exportToXLS(selectedYear);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-3"
                                        data-testid="export-xlsx-btn"
                                    >
                                        <FileText className="w-4 h-4 text-blue-600" />
                                        <span>Export as Excel</span>
                                    </button>

                                    <div className="border-t border-slate-100 my-1" />
                                    <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 uppercase">All Years</div>
                                    <button
                                        onClick={() => {
                                            setShowExportMenu(false);
                                            exportToCSV(null);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-3"
                                        data-testid="export-all-csv-btn"
                                    >
                                        <FileText className="w-4 h-4 text-green-600" />
                                        <span>All Years (CSV)</span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowExportMenu(false);
                                            exportToXLS(null);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-3"
                                        data-testid="export-all-xlsx-btn"
                                    >
                                        <FileText className="w-4 h-4 text-blue-600" />
                                        <span>All Years (Excel)</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="overflow-x-auto pb-32">
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th
                                className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                                onClick={() => handleSort('entryDate')}
                            >
                                <div className="flex items-center space-x-1">
                                    <span>Date Added</span>
                                    {sortField === 'entryDate' && (
                                        sortDirection === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />
                                    )}
                                </div>
                            </th>
                            <th
                                className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                                onClick={() => handleSort('date')}
                            >
                                <div className="flex items-center space-x-1">
                                    <span>Activity Date</span>
                                    {sortField === 'date' && (
                                        sortDirection === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />
                                    )}
                                </div>
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Reporting Period</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Name</th>
                            <th
                                className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                                onClick={() => handleSort('quantity')}
                            >
                                <div className="flex items-center justify-end space-x-1">
                                    <span>Quantity</span>
                                    {sortField === 'quantity' && (
                                        sortDirection === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />
                                    )}
                                </div>
                            </th>
                            <th
                                className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                                onClick={() => handleSort('co2')}
                            >
                                <div className="flex items-center justify-end space-x-1">
                                    <span>Emissions</span>
                                    {sortField === 'co2' && (
                                        sortDirection === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />
                                    )}
                                </div>
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {(() => {
                            const emissionItems = recentEntriesByDateAdded.map(e => ({
                                id: e.id,
                                type: 'emission',
                                date: e.start_date || e.entry_date || e.created_at,
                                entryDate: e.entry_date || e.created_at,
                                name: getCategoryInfo(e.category).label,
                                quantity: e.quantity,
                                unit: e.unit,
                                co2: e.co2_equivalent || 0,
                                documents: e.documents,
                                raw: e
                            }));

                            const purchaseItems = recentActivity
                                .filter(a => a.type === 'offset_purchase')
                                .map(o => ({
                                    id: o.id,
                                    type: 'offset_purchase',
                                    date: o.date,
                                    entryDate: o.date,
                                    name: o.project_name || o.description || 'Carbon Credit',
                                    quantity: o.amount,
                                    unit: 'tCO₂-e',
                                    co2: o.co2_equivalent || o.amount,
                                    documents: null,
                                    raw: o
                                }));

                            const allocationItems = recentActivity
                                .filter(a => a.type === 'offset_applied')
                                .map(o => ({
                                    id: o.id,
                                    type: 'offset_applied',
                                    date: o.date,
                                    entryDate: o.date,
                                    name: o.project_name || o.description || 'Carbon Credit',
                                    quantity: o.amount,
                                    unit: 'tCO₂-e',
                                    co2: o.co2_equivalent || o.amount,
                                    documents: null,
                                    raw: o
                                }));

                            const allEntriesSorted = [...emissionItems, ...purchaseItems, ...allocationItems]
                                .sort((a, b) => {
                                    let comparison = 0;
                                    switch (sortField) {
                                        case 'entryDate':
                                            comparison = new Date(b.entryDate) - new Date(a.entryDate);
                                            break;
                                        case 'date':
                                            comparison = new Date(b.date) - new Date(a.date);
                                            break;
                                        case 'quantity':
                                            comparison = parseFloat(b.quantity || 0) - parseFloat(a.quantity || 0);
                                            break;
                                        case 'co2':
                                            comparison = parseFloat(b.co2 || 0) - parseFloat(a.co2 || 0);
                                            break;
                                        default:
                                            comparison = new Date(b.entryDate) - new Date(a.entryDate);
                                    }
                                    return sortDirection === 'desc' ? comparison : -comparison;
                                })
                                .slice(0, 10);

                            return allEntriesSorted.map((entry) => {
                                const isOffset = entry.type === 'offset_purchase' || entry.type === 'offset_applied';
                                const isPurchase = entry.type === 'offset_purchase';
                                const isApplied = entry.type === 'offset_applied';
                                const hasEvidence = !isOffset && entry.documents && entry.documents.length > 0;
                                const hasOffsetEvidence = isOffset && entry.raw?.certificate_url;

                                let badgeClass = 'bg-red-100 text-red-700';
                                let badgeLabel = 'Emission Entry Added';
                                let badgeIcon = <Flame className="w-3 h-3 mr-1 flex-shrink-0" />;
                                if (isPurchase) {
                                    badgeClass = 'bg-blue-100 text-blue-700';
                                    badgeLabel = 'Carbon Credit Purchased';
                                    badgeIcon = <Leaf className="w-3 h-3 mr-1 flex-shrink-0" />;
                                } else if (isApplied) {
                                    badgeClass = 'bg-emerald-100 text-emerald-700';
                                    badgeLabel = 'Carbon Offset Retired';
                                    badgeIcon = <Leaf className="w-3 h-3 mr-1 flex-shrink-0" />;
                                }

                                const startDate = entry.raw?.start_date || entry.date;
                                const endDate = entry.raw?.end_date;
                                const entryDateAdded = entry.raw?.entry_date || entry.raw?.created_at || entry.date;

                                const reportingPeriod = isOffset
                                    ? getFYLabel(selectedYear)
                                    : getReportingPeriods(startDate, endDate || startDate);

                                const stateTimezones = {
                                    'NSW': 'Australia/Sydney', 'VIC': 'Australia/Melbourne', 'QLD': 'Australia/Brisbane',
                                    'SA': 'Australia/Adelaide', 'WA': 'Australia/Perth', 'TAS': 'Australia/Hobart',
                                    'NT': 'Australia/Darwin', 'ACT': 'Australia/Sydney'
                                };
                                const timezone = stateTimezones[currentOrg?.state] || 'Australia/Sydney';

                                return (
                                    <tr key={`${entry.type}-${entry.id}`} className="hover:bg-slate-50" data-testid={isOffset ? "offset-entry" : "emission-entry"}>
                                        <td className="px-4 py-3 text-sm text-slate-600 border-l-2 border-transparent group-hover:border-[#003B5C]">
                                            <div className="flex flex-col">
                                                <span>{formatEntryDate(entryDateAdded)}</span>
                                                <span className="text-xs text-slate-400">
                                                    {new Date(entryDateAdded).toLocaleTimeString('en-AU', {
                                                        hour: '2-digit', minute: '2-digit', hour12: false, timeZone: timezone
                                                    })}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-slate-600">
                                            {isOffset
                                                ? new Date(entry.date).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: '2-digit' })
                                                : formatActivityDate(startDate, endDate)
                                            }
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`text-xs font-medium px-2 py-1 rounded ${reportingPeriod.includes(',')
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'bg-slate-100 text-slate-700'
                                                }`}>
                                                {reportingPeriod}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center space-x-1.5">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${badgeClass}`}>
                                                    {badgeIcon}
                                                    {badgeLabel}
                                                </span>
                                                {(hasEvidence || hasOffsetEvidence) && (
                                                    <span className="text-[#003B5C]" title="Evidence attached">
                                                        <Paperclip className="w-3.5 h-3.5" />
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm font-medium text-[#003B5C]">{entry.name}</td>
                                        <td className="px-4 py-3 text-sm text-slate-700 text-right">{entry.quantity} {entry.unit}</td>
                                        <td className={`px-4 py-3 text-sm font-semibold text-right ${isApplied || (Math.abs(entry.co2) === 0 && !isPurchase) ? 'text-emerald-600' : isPurchase ? 'text-blue-600' : 'text-red-600'}`}>
                                            {isApplied ? '-' : isPurchase ? '' : '+'}
                                            {Math.abs(entry.co2) < 0.1 && Math.abs(entry.co2) > 0
                                                ? Math.abs(entry.co2).toFixed(4)
                                                : Math.abs(entry.co2).toFixed(2)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-center relative">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setOpenActionMenu(openActionMenu === entry.id ? null : entry.id)}
                                                    className="text-slate-500 hover:bg-slate-100 h-8 w-8 p-0"
                                                    data-testid={`action-menu-${entry.id}`}
                                                >
                                                    <MoreVertical className="w-4 h-4" />
                                                </Button>

                                                {openActionMenu === entry.id && (
                                                    <div className="absolute right-0 top-8 z-50 bg-white rounded-lg shadow-lg border border-slate-200 py-1 min-w-[160px]">
                                                        {hasEvidence && (
                                                            <button
                                                                onClick={async () => {
                                                                    const doc = entry.documents[0];
                                                                    const { data, error } = await supabase.storage
                                                                        .from('evidence-vault')
                                                                        .createSignedUrl(doc.storage_path, 3600);
                                                                    if (error) {
                                                                        toast.error('Could not access file');
                                                                        return;
                                                                    }
                                                                    window.open(data.signedUrl, '_blank');
                                                                    setOpenActionMenu(null);
                                                                }}
                                                                className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-2 text-[#003B5C]"
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                                <span>View Evidence</span>
                                                            </button>
                                                        )}
                                                        {hasOffsetEvidence && (
                                                            <button
                                                                onClick={() => { window.open(entry.raw.certificate_url, '_blank'); setOpenActionMenu(null); }}
                                                                className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-2 text-[#003B5C]"
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                                <span>View Certificate</span>
                                                            </button>
                                                        )}

                                                        {!isOffset ? (
                                                            <>
                                                                {!canWithReason(FEATURES.EMISSIONS_EDIT_ENTRY).isHidden && (
                                                                    <button
                                                                        onClick={() => { handleEditEntry(entry.raw); setOpenActionMenu(null); }}
                                                                        className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-2 text-blue-600"
                                                                    >
                                                                        <Settings className="w-4 h-4" />
                                                                        <span>Edit</span>
                                                                    </button>
                                                                )}
                                                                <button
                                                                    onClick={() => { fetchEntryHistory(entry.id); setOpenActionMenu(null); }}
                                                                    className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-2 text-slate-600"
                                                                >
                                                                    <History className="w-4 h-4" />
                                                                    <span>History</span>
                                                                </button>
                                                                {!hasEvidence ? (
                                                                    !canWithReason(FEATURES.EMISSIONS_EVIDENCE_MANAGE).isHidden && (
                                                                        <button
                                                                            onClick={() => { setShowEvidenceUpload(entry.id); setOpenActionMenu(null); }}
                                                                            className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-2 text-[#74A57F]"
                                                                        >
                                                                            <Paperclip className="w-4 h-4" />
                                                                            <span>Attach Evidence</span>
                                                                        </button>
                                                                    )
                                                                ) : (
                                                                    <>
                                                                        {!canWithReason(FEATURES.EMISSIONS_EVIDENCE_MANAGE).isHidden && (
                                                                            showEvidenceDeleteConfirm === entry.id ? (
                                                                                <div className="px-4 py-2 flex items-center justify-between bg-red-50">
                                                                                    <span className="text-xs text-red-600 font-medium">Delete evidence?</span>
                                                                                    <div className="flex space-x-1">
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="sm"
                                                                                            onClick={() => {
                                                                                                handleDeleteEvidence(entry);
                                                                                                setShowEvidenceDeleteConfirm(null);
                                                                                                setOpenActionMenu(null);
                                                                                            }}
                                                                                            disabled={deletingEvidence}
                                                                                            className="text-white bg-red-600 hover:bg-red-700 h-6 w-6 p-0 rounded-md"
                                                                                        >
                                                                                            {deletingEvidence ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                                                                        </Button>
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="sm"
                                                                                            onClick={() => setShowEvidenceDeleteConfirm(null)}
                                                                                            className="text-slate-500 hover:bg-slate-200 h-6 w-6 p-0 rounded-md bg-white border border-slate-200"
                                                                                        >
                                                                                            <X className="w-3 h-3" />
                                                                                        </Button>
                                                                                    </div>
                                                                                </div>
                                                                            ) : (
                                                                                <button
                                                                                    onClick={() => setShowEvidenceDeleteConfirm(entry.id)}
                                                                                    disabled={deletingEvidence}
                                                                                    className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 flex items-center space-x-2 text-red-500"
                                                                                >
                                                                                    {deletingEvidence ? (
                                                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                                                    ) : (
                                                                                        <Trash2 className="w-4 h-4" />
                                                                                    )}
                                                                                    <span>Delete Evidence</span>
                                                                                </button>
                                                                            )
                                                                        )}
                                                                    </>
                                                                )}
                                                                {!canWithReason(FEATURES.EMISSIONS_DELETE_ENTRY).isHidden && (
                                                                    <>
                                                                        <div className="border-t border-slate-100 my-1"></div>
                                                                        {showDeleteConfirm === entry.id ? (
                                                                            <div className="px-4 py-2 flex items-center justify-between">
                                                                                <span className="text-xs text-red-600">Confirm delete?</span>
                                                                                <div className="flex space-x-1">
                                                                                    <Button
                                                                                        variant="ghost"
                                                                                        size="sm"
                                                                                        onClick={() => { handleDeleteEntry(entry.id); setOpenActionMenu(null); }}
                                                                                        disabled={deletingEntry === entry.id}
                                                                                        className="text-red-600 hover:bg-red-50 h-6 w-6 p-0"
                                                                                    >
                                                                                        {deletingEntry === entry.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                                                                    </Button>
                                                                                    <Button
                                                                                        variant="ghost"
                                                                                        size="sm"
                                                                                        onClick={() => setShowDeleteConfirm(null)}
                                                                                        className="text-slate-500 hover:bg-slate-100 h-6 w-6 p-0"
                                                                                    >
                                                                                        <X className="w-3 h-3" />
                                                                                    </Button>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <button
                                                                                onClick={() => setShowDeleteConfirm(entry.id)}
                                                                                className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 flex items-center space-x-2 text-red-500"
                                                                            >
                                                                                <Trash2 className="w-4 h-4" />
                                                                                <span>Delete</span>
                                                                            </button>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <button
                                                                onClick={() => { navigate('/offsets'); setOpenActionMenu(null); }}
                                                                className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-2 text-emerald-600"
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                                <span>Manage Offsets</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            });
                        })()}
                    </tbody>
                </table>

                {totalEntriesCount > 10 && (
                    <div className="text-center pt-4 border-t border-slate-200 mt-4">
                        <Link
                            to="/entries"
                            className="inline-flex items-center text-[#003B5C] hover:text-[#74A57F] font-medium transition-colors"
                            data-testid="view-all-entries-link"
                        >
                            View All {totalEntriesCount} Entries
                            <ChevronRight className="w-4 h-4 ml-1" />
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmissionsTable;
